import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, orderBy, getDocs, limit, where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  getLostItemCase, lostItemStatusBadge,
  adminOverrideLostItem, getDisputeConfig,
} from '../../services/disputeService';
import toast from 'react-hot-toast';

const TERMINAL = ['resolved_returned', 'resolved_collected', 'unresolved',
  'expired_driver_custody', 'unresolved_pickup_timeout', 'cancelled_by_rider', 'admin_resolved'];

export default function AdminLostItems() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [selectedCase, setSelectedCase] = useState(null);

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'lostItems'), orderBy('createdAt', 'desc'), limit(100));
    getDocs(q)
      .then((snap) => setCases(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch((err) => toast.error(err.message || 'Could not load cases'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!caseId) { setSelectedCase(null); return; }
    getLostItemCase(caseId)
      .then(setSelectedCase)
      .catch((err) => toast.error(err.message || 'Could not load case'));
  }, [caseId]);

  const filtered = cases.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'open') return !TERMINAL.includes(c.status);
    if (filter === 'escalated') return c.adminEscalatedAt != null;
    if (filter === 'refund_review') return c.status === 'admin_review_refund';
    if (filter === 'closed') return TERMINAL.includes(c.status);
    return true;
  });

  const escalatedCount = cases.filter(c => c.adminEscalatedAt != null && !TERMINAL.includes(c.status)).length;
  const refundReviewCount = cases.filter(c => c.status === 'admin_review_refund').length;

  return (
    <div className="p-6">
      <h1 className="font-serif mt-0 mb-2">Lost items</h1>
      <p className="text-body text-[0.9rem] mb-4">
        All lost item cases. Open cases require admin attention or are in active state.
      </p>

      <div className="flex gap-1.5 mb-4 flex-wrap">
        {[
          { key: 'open', label: 'Open' },
          { key: 'escalated', label: `Escalated${escalatedCount > 0 ? ` (${escalatedCount})` : ''}` },
          { key: 'refund_review', label: `Refund review${refundReviewCount > 0 ? ` (${refundReviewCount})` : ''}` },
          { key: 'closed', label: 'Closed' },
          { key: 'all', label: 'All' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full border-[1.5px] cursor-pointer font-semibold text-[0.78rem] ${
              filter === f.key ? 'border-brand-primary text-brand-primary' : 'border-slate-200 bg-white text-body'
            }`}
            style={filter === f.key ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{f.label}</button>
        ))}
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: caseId ? '350px 1fr' : '1fr' }}>
        {/* List */}
        <div>
          {loading && <div className="p-5 text-muted">Loading...</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-5 text-muted bg-white rounded-xl border border-slate-200">
              No cases.
            </div>
          )}
          <div className="grid gap-2">
            {filtered.map(c => {
              const badge = lostItemStatusBadge(c.status);
              const isSelected = c.id === caseId;
              const isEscalated = c.adminEscalatedAt != null && !TERMINAL.includes(c.status);
              return (
                <div key={c.id} onClick={() => navigate(`/admin/lost-items/${c.id}`)}
                  className="p-3 rounded-[10px] bg-white cursor-pointer text-[0.85rem]"
                  style={{ border: `${isSelected ? '2px' : '1px'} solid ${isSelected ? '#2dd4a8' : isEscalated ? '#dc2626' : '#e2e8f0'}` }}>
                  <div className="flex justify-between items-start mb-1">
                    <strong className="text-brand-dark">{c.referenceNumber}</strong>
                    <span className="px-2 py-0.5 rounded-full text-[0.68rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                  {isEscalated && (
                    <div className="text-[0.75rem] text-red-600 font-semibold mb-1">
                      ⚠ Escalated
                    </div>
                  )}
                  <div className="text-body text-[0.78rem]">
                    Rider: {c.riderName} · Driver: {c.driverName || '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        {caseId && (
          <AdminLostItemDetail caseData={selectedCase} onUpdated={() => {
            getLostItemCase(caseId).then(setSelectedCase);
            // Also refresh list
            const q = query(collection(db, 'lostItems'), orderBy('createdAt', 'desc'), limit(100));
            getDocs(q).then((snap) => setCases(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
          }} />
        )}
      </div>
    </div>
  );
}

// ───────── DETAIL ─────────

function AdminLostItemDetail({ caseData, onUpdated }) {
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [override, setOverride] = useState({
    newStatus: 'admin_resolved',
    reason: '',
    refundRider: false,
    payoutDriver: false,
  });
  const [config, setConfig] = useState(null);

  useEffect(() => { getDisputeConfig().then(setConfig).catch(() => {}); }, []);

  if (!caseData) {
    return <div className="p-5 bg-white rounded-xl border border-slate-200">Loading case...</div>;
  }

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const badge = lostItemStatusBadge(caseData.status);
  const isTerminal = TERMINAL.includes(caseData.status);

  const handleOverride = async () => {
    if (!override.reason || override.reason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setActionInProgress(true);
    try {
      await adminOverrideLostItem({
        caseId: caseData.id,
        newStatus: override.newStatus,
        reason: override.reason,
        refundRider: override.refundRider,
        payoutDriver: override.payoutDriver,
      });
      toast.success('Case resolved by admin');
      setShowOverride(false);
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err.message || 'Override failed');
    }
    setActionInProgress(false);
  };

  return (
    <div className="p-5 bg-white rounded-xl border border-slate-200 max-h-[85vh] overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h2 className="m-0 text-[1.2rem]">{caseData.referenceNumber}</h2>
          <div className="text-[0.78rem] text-muted mt-0.5">
            Created {fmtDate(caseData.createdAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Parties */}
      <div className="p-3 rounded-[10px] bg-slate-50 mb-3 text-[0.82rem]">
        <div><strong>Rider:</strong> {caseData.riderName} ({caseData.riderId?.substring(0, 8)})</div>
        <div><strong>Driver:</strong> {caseData.driverName || '—'} ({caseData.driverId?.substring(0, 8)})</div>
        <div><strong>Trip:</strong> {caseData.bookingReferenceNumber || caseData.bookingId?.substring(0, 8)}</div>
      </div>

      {/* Item description */}
      <div className="mb-3">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1">
          Description
        </div>
        <div className="text-[0.85rem] text-brand-dark whitespace-pre-wrap">{caseData.description}</div>
        <div className="text-[0.78rem] text-body mt-1">
          Location: <strong>{caseData.locationInVehicle?.replace(/_/g, ' ')}</strong>
        </div>
      </div>

      {/* Photos */}
      {(caseData.reportPhotos?.length > 0 || caseData.driverPhotos?.length > 0) && (
        <div className="mb-3">
          {caseData.reportPhotos?.length > 0 && (
            <>
              <div className="text-[0.78rem] text-muted font-semibold mb-1">Rider photos:</div>
              <div className="flex flex-wrap gap-1 mb-2">
                {caseData.reportPhotos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={`R${i + 1}`} className="w-[60px] h-[60px] object-cover rounded-md border border-slate-200" />
                  </a>
                ))}
              </div>
            </>
          )}
          {caseData.driverPhotos?.length > 0 && (
            <>
              <div className="text-[0.78rem] text-muted font-semibold mb-1">Driver photos:</div>
              <div className="flex flex-wrap gap-1">
                {caseData.driverPhotos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt={`D${i + 1}`} className="w-[60px] h-[60px] object-cover rounded-md border border-slate-200" />
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Key facts */}
      <div className="p-3 rounded-[10px] bg-slate-50 mb-3 text-[0.82rem]">
        {caseData.driverResponse && (
          <div><strong>Driver response:</strong> {caseData.driverResponse.replace(/_/g, ' ')} ({fmtDate(caseData.driverRespondedAt)})</div>
        )}
        {caseData.driverNotes && <div className="italic mt-1">"{caseData.driverNotes}"</div>}
        {caseData.returnFeeOffered != null && (
          <div><strong>Return fee offered:</strong> ${caseData.returnFeeOffered.toFixed(2)}</div>
        )}
        {caseData.returnChargedAt && (
          <div><strong>Rider charged:</strong> {fmtDate(caseData.returnChargedAt)} (PI: {caseData.returnPaymentIntentId?.substring(0, 12)}...)</div>
        )}
        {caseData.returnMarkedDeliveredAt && (
          <div><strong>Driver marked delivered:</strong> {fmtDate(caseData.returnMarkedDeliveredAt)}</div>
        )}
        {caseData.payoutAt && (
          <div><strong>Driver paid out:</strong> {fmtDate(caseData.payoutAt)} (ID: {caseData.payoutId?.substring(0, 12)}...)</div>
        )}
        {caseData.custodyDeadline && !isTerminal && (
          <div><strong>Custody expires:</strong> {fmtDate(caseData.custodyDeadline)}</div>
        )}
        {caseData.adminEscalatedAt && (
          <div className="text-red-600 font-semibold">⚠ Escalated to admin: {fmtDate(caseData.adminEscalatedAt)}</div>
        )}
      </div>

      {/* Refund request details */}
      {caseData.refundRequestedAt && (
        <div className="p-3 rounded-[10px] mb-3 text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          <div className="font-semibold mb-1">Refund requested {fmtDate(caseData.refundRequestedAt)}</div>
          {caseData.refundRequestReason && <div className="italic">"{caseData.refundRequestReason}"</div>}
        </div>
      )}

      {/* Admin override panel */}
      {!isTerminal && (
        <>
          {!showOverride ? (
            <button onClick={() => setShowOverride(true)}
              className="w-full p-2.5 rounded-[10px] border-[1.5px] bg-white cursor-pointer font-semibold text-[0.85rem]"
              style={{ borderColor: '#ff6b6b', color: '#ff6b6b' }}>Admin override / force resolve</button>
          ) : (
            <div className="p-3.5 rounded-[10px] bg-white border-[1.5px]" style={{ borderColor: '#ff6b6b' }}>
              <h3 className="mt-0 mb-2.5 text-[0.95rem] text-red-600">Admin override</h3>

              <div className="form-group mb-2">
                <label className="form-label text-[0.82rem]">Resolve as *</label>
                <select className="form-input form-select" value={override.newStatus}
                  onChange={(e) => setOverride({ ...override, newStatus: e.target.value })}>
                  <option value="admin_resolved">Admin resolved (generic)</option>
                  <option value="resolved_returned">Resolved — returned</option>
                  <option value="resolved_collected">Resolved — collected</option>
                  <option value="unresolved">Unresolved (item lost)</option>
                  <option value="expired_driver_custody">Driver custody expired</option>
                  <option value="cancelled_by_rider">Cancelled by rider</option>
                </select>
              </div>

              <div className="form-group mb-2">
                <label className="form-label text-[0.82rem]">Reason * (min 10 chars)</label>
                <textarea className="form-input" rows={2}
                  placeholder="Explain why admin is forcing this resolution"
                  value={override.reason}
                  onChange={(e) => setOverride({ ...override, reason: e.target.value })} />
              </div>

              {caseData.returnPaymentIntentId && (
                <label className="flex items-center gap-1.5 text-[0.82rem] mb-1.5">
                  <input type="checkbox" checked={override.refundRider}
                    onChange={(e) => setOverride({ ...override, refundRider: e.target.checked })} />
                  Refund rider ${caseData.returnFeeOffered?.toFixed(2)}
                </label>
              )}
              {caseData.returnFeeOffered && !caseData.payoutAt && (
                <label className="flex items-center gap-1.5 text-[0.82rem] mb-2.5">
                  <input type="checkbox" checked={override.payoutDriver}
                    onChange={(e) => setOverride({ ...override, payoutDriver: e.target.checked })} />
                  Payout driver ${caseData.returnFeeOffered?.toFixed(2)}
                </label>
              )}

              <div className="flex gap-1.5">
                <button onClick={() => setShowOverride(false)}
                  className="flex-1 p-2 rounded-lg border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.82rem]">Cancel</button>
                <button onClick={handleOverride} disabled={actionInProgress}
                  className="flex-1 p-2 rounded-lg border-0 bg-red-600 text-white cursor-pointer font-bold text-[0.82rem]">{actionInProgress ? '...' : 'Confirm override'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Status history */}
      {caseData.statusHistory && caseData.statusHistory.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-[0.82rem] text-body font-semibold">
            Case history ({caseData.statusHistory.length})
          </summary>
          <div className="mt-1.5 p-2.5 rounded-lg bg-slate-50 text-[0.78rem] text-body">
            {caseData.statusHistory.map((h, i) => (
              <div key={i} className="mb-1.5 pb-1.5" style={{ borderBottom: i < caseData.statusHistory.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <div><strong>{h.from || '—'}</strong> → <strong>{h.to}</strong></div>
                {h.note && <div className="italic">"{h.note}"</div>}
                <div className="text-muted">{fmtDate(h.at)} by {h.by === 'system' ? 'system' : h.by?.substring(0, 8)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}